import {useGetCurrentUser, useGetUsersSessions} from "api/api-hooks/user-hooks";
import {IUserSessionData} from "api/model/user/IUserSessionData";
import {UserDTO} from "api/model/user/UserDTO";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
    getFirstDayOfLastMonth,
    getStartDate,
    GroupingType
} from "components/pages/users-statistics-dashboard/stats-utils";
import EasasPageTemplateWrapper from "components/templates/EasasPageTemplateWrapper";
import {Dropdown} from "primereact/dropdown";
import React, {useEffect, useState} from "react";
import UserStatisticsDatatable from "./components/user-statistics-datatable/UserStatisticsDatatable";
import UserStatisticsGanttChart from "./components/users-statistics-gantt-chart/UsersStatisticsGanttChart";


function generateCompanyListOptions(userSessionData: IUserSessionData[]): DropdownSelectionOptions[] {
    const companyList: Set<string> = new Set();
    userSessionData.forEach((user: any) => {
        companyList.add(user.companyRef);
    });
    return Array.from(companyList).map((company) => {
        return {label: company, value: company};
    });
}

function generateJobTitleOptions(userSessionData: IUserSessionData[]): DropdownSelectionOptions[] {
    const titlesList: Set<string> = new Set();
    userSessionData.forEach((user: any) => {
        titlesList.add(user.jobTitle);
    });
    return Array.from(titlesList).map((title) => {
        if (title === undefined) {
            return {label: "No title", value: ""};
        } else {
            return {label: title, value: title};
        }
    });
}

interface DropdownSelectionOptions {
    label: string;
    value: string | number;
}

const groupOptions: DropdownSelectionOptions[] = [
    {label: "User", value: "username"},
    {label: "Job Title", value: "jobTitle"},
];

const timeSpanOptions: DropdownSelectionOptions[] = [
    {label: "Last 7 Days", value: 7},
    {label: "Last 30 Days", value: 30},
    {label: "Last 90 Days", value: 90},
    {label: "Last Year", value: 365}
];

const UsersStatisticsDashboard = () => {
    const [dateFrom, setDateFrom] = useState<Date>(getFirstDayOfLastMonth());
    const [dateTo, setDateTo] = useState<Date>(new Date());

    const [userSessionData, setUserSessionData] = useState<IUserSessionData[]>([]);
    const [currentUser, setCurrentUser] = useState<UserDTO>();
    const [groupBy, setGroupBy] = useState<GroupingType>("username");
    const [companyFilter, setCompanyFilter] = useState<string>();
    const [jobTitleFilter, setJobTitleFilter] = useState<string>();
    const [companyListOptions, setCompanyListOptions] = useState<DropdownSelectionOptions[]>([]);
    const [jobTitleOptions, setJobTitleOptions] = useState<DropdownSelectionOptions[]>([]);
    const [timeSpanSelection, setTimeSpanSelection] = useState(0);

    const usersSessionsQuery = useGetUsersSessions(dateFrom.toISOString(), dateTo.toISOString(), companyFilter, jobTitleFilter, {
        refetchInterval: 10000,
    });

    const currentUserQuery = useGetCurrentUser();

    useEffect(() => {
        if (usersSessionsQuery.isSuccess) {
            const data = usersSessionsQuery.data;
            setUserSessionData(data);
            setCompanyListOptions(generateCompanyListOptions(data));
            setJobTitleOptions(generateJobTitleOptions(data));
        } else if (usersSessionsQuery.isError) {
            const error = usersSessionsQuery.error;
            easasAlert("error", `${error.message}`, "error");
        }
    }, [usersSessionsQuery.isSuccess, usersSessionsQuery.isError, usersSessionsQuery.data, usersSessionsQuery.error]);

    useEffect(() => {
        if (currentUserQuery.isSuccess) {
            const data = currentUserQuery.data;
            setCurrentUser(data);
        } else if (currentUserQuery.isError) {
            easasAlert("Error", "Failed to get current user", "error");
        }
    }, [currentUserQuery.isSuccess, currentUserQuery.isError, currentUserQuery.data]);

    useEffect(() => {
        usersSessionsQuery.refetch();
    }, [companyFilter, jobTitleFilter]);

    const handleTimeSpanChange = (selectedTimeSpan: number) => {
        const newDateFrom = getStartDate(selectedTimeSpan);
        setTimeSpanSelection(selectedTimeSpan);
        setDateFrom(newDateFrom);
        setDateTo(new Date());
    };

    const handleGroupingChange = (e: any) => {
        setGroupBy(e.value)
        setJobTitleFilter(undefined)
    }

    const handleDateChange = (e: any, type: string) => {
       setTimeSpanSelection(0)
        switch (type) {
            case "from":
                setDateFrom(e)
                break;
            case "to":
                setDateTo(e)
                break;
            default:
                break;
        }

    }

    return (
        <EasasPageTemplateWrapper cardStyle={{margin: "auto", height: "calc(100% - 1rem)" }}>
            <div className="flex justify-content-between align-items-center">
                <div className="flex gap-2 mb-2">
                    <div className="flex flex-column gap-2">
                        <label>Date from:</label>
                        <CalendarWrapper value={dateFrom} showTime={false} onChange={e => handleDateChange(e, "from")}/>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label>Date to:</label>
                        <CalendarWrapper value={dateTo} showTime={false} onChange={e => handleDateChange(e, "to")}/>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label>Time Span: </label>
                        <Dropdown
                            placeholder="Select"
                            value={timeSpanSelection}
                            options={timeSpanOptions}
                            onChange={(e) => handleTimeSpanChange(e.value)}
                        />
                    </div>
                </div>
                <div className="flex gap-2 ">
                    {currentUser && currentUser.effectivePermissions.includes("ADMIN") &&
                        <div className="flex flex-column gap-2">
                            <label>Company: </label>
                            <Dropdown
                                value={companyFilter}
                                placeholder="Select a Company"
                                options={companyListOptions}
                                onChange={e => setCompanyFilter(e.value)}
                                showClear
                            />
                        </div>}
                    <div className="flex flex-column gap-2">
                        <label>Group By: </label>
                        <Dropdown
                            value={groupBy}
                            options={groupOptions}
                            onChange={handleGroupingChange}
                        />
                    </div>
                    {groupBy === "jobTitle" && <div className="flex flex-column gap-2">
                        <label>Job Title</label>
                        <Dropdown
                            placeholder="Select Job"
                            value={jobTitleFilter}
                            options={jobTitleOptions}
                            onChange={e => setJobTitleFilter(e.value)}
                            showClear
                        />
                    </div>}
                </div>
            </div>
            <UserStatisticsGanttChart
                userSessionData={userSessionData}
                groupBy={groupBy}
                companyFilter={companyFilter}
                jobTitleFilter={jobTitleFilter}
            />
            <UserStatisticsDatatable
                userSessionData={userSessionData}
                groupBy={groupBy}
                companyFilter={companyFilter}
                jobTitleFilter={jobTitleFilter}
            />
        </EasasPageTemplateWrapper>
    );
};

export default UsersStatisticsDashboard;